import React, { useCallback, useEffect, useState } from 'react';
import { Percent, ExternalLink } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { Toggle } from './CardProcessingOptionsCard';
/* The SAME agreements list the location's card-processing page uses — upload,
   set-active and delete are one flow, so no two screens can drift into
   disagreeing about what is on file for a location. */
import SurchargeAgreementsList from './SurchargeAgreementsList';

/** Small neutral pill, matching the order lanes' own chips. */
const Chip: React.FC<{ tone?: 'ok' | 'bad'; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold ${
    tone === 'ok'
      ? 'border border-emerald-300 bg-emerald-50 text-emerald-700'
      : tone === 'bad'
        ? 'border border-rose-300 bg-rose-50 text-rose-700'
        : 'border border-stone-300 bg-stone-50 text-stone-600'}`}
  >
    {children}
  </span>
);

/**
 * THE APPROVAL HALF OF SURCHARGE, on the order's card-processing lane.
 *
 * It belongs beside the worksheet rather than on a device card, because it is
 * not a per-device fact: one signed agreement covers every till in the shop,
 * and its rate and flat fee are the ceiling all of them charge under. Asking
 * it per reader would be asking the same question twice and inviting two
 * answers.
 *
 * The ISO (or we) upload the signed PDF here with its ceilings, then approve
 * the location. ONLY THEN does the per-register switch on the POS device card
 * come alive — that gate is `surchargeApproved` in PaymentTypesPanel, which
 * reads exactly the two fields this section writes.
 *
 * The list is the SAME component the location's card-processing page uses, so
 * an agreement uploaded on either screen is the one agreement, and the two can
 * never drift into disagreeing about what is on file.
 */
export const SurchargeApprovalSection: React.FC<{
  tenantId: string;
  locationId: string;
  locationName?: string;
  onChanged: () => void;
}> = ({ tenantId, locationId, locationName, onChanged }) => {
  const [gateway, setGateway] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  /*
    WHY THE READ FAILED, kept rather than swallowed.

    This used to catch and set `gateway` to null, which rendered as "not
    approved · Agreements (0)". That is indistinguishable from a location
    that genuinely has no agreement — so a 401, an expired session or a
    service error all read as a settled, wrong fact about a legal document,
    and an operator who had just uploaded an agreement was told it did not
    exist. On a screen that decides whether surcharging is lawful, "I could
    not find out" has to look nothing like "no".
  */
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const gw: any = await api.posNmi.getPaymentGateway(tenantId, locationId);
      /*
        THE CONFIG IS AT THE ROOT. `gateway` on this record is the PROVIDER
        ('nmi', 'dejavoo', …), not a nested config — so unwrapping through it
        resolved to a STRING, and every field read off it came back undefined.
        A location with a live, active agreement rendered "Agreements (0) ·
        not approved", which is the most dangerous way for this screen to be
        wrong: it reads as a settled fact about a legal document.
      */
      setGateway(gw ?? null);
    } catch (e: any) {
      const status = e?.status ?? e?.statusCode;
      setGateway(null);
      setError(status === 401 || status === 403
        /* Said specifically: the fix is to sign in again, and no amount of
           retrying this call will do it. */
        ? 'Your session is not authorised to read this location’s payment settings — sign in again, then retry.'
        : (e?.serverMessage || e?.message || 'Could not read this location’s surcharge settings.'));
    }
  }, [tenantId, locationId]);

  useEffect(() => { void load(); }, [load]);

  /*
    AFTER AN UPLOAD, TRUST WHAT COMES BACK — then re-read.

    The list's own calls (upload, set-active, delete) have already changed the
    record by the time it tells us. Re-reading is what refreshes the row, and
    it is the ONLY thing that does, so a failed re-read must not be silent:
    it leaves the section showing the state from before the upload, which is
    exactly the "I created it and nothing happened" this fixes.
  */
  const refresh = useCallback(async () => {
    await load();
    onChanged();
  }, [load, onChanged]);

  const agreements: any[] = Array.isArray(gateway?.surchargeAgreements) ? gateway.surchargeAgreements : [];
  const active = agreements.find((a) => a.id === gateway?.activeSurchargeAgreementId) || null;
  const approved = !!gateway?.surchargeEnabled && !!active;

  /**
   * Write the flag AND the ceilings together, always.
   *
   * Approving without re-seeding the two maxima would leave a location
   * approved to surcharge up to 0% — approved and unable to charge, which
   * reads as a POS bug rather than a missing seed. The agreement is the
   * source of both numbers; nothing here is typed.
   */
  const writeApproval = async (on: boolean, agreement: any) => {
    await api.posNmi.putPaymentGateway(tenantId, locationId, {
      surchargeEnabled: on,
      activeSurchargeAgreementId: agreement?.id ?? null,
      maxSurchargeRate: Number(agreement?.maxRate || 0),
      maxSurchargeFlat: Number(agreement?.maxFlat || 0),
    });
  };

  const approve = async (on: boolean) => {
    /*
      THE GATE, SAID RATHER THAN SENT AND REFUSED. The service rejects the flag
      with no agreement behind it, and answers with a code that tells an
      operator nothing about what to do next.
    */
    if (on && !active) {
      toastService.error(agreements.length === 0
        ? 'Upload the signed surcharge agreement first — there is nothing to approve without one.'
        : 'Set one agreement active first — its rate and flat fee are the ceiling every till charges under.');
      return;
    }
    setBusy(true);
    try {
      await writeApproval(on, active);
      toastService.success(on
        ? `Surcharge approved for ${locationName || 'this location'} — up to ${Number(active?.maxRate || 0).toFixed(2)}% + $${Number(active?.maxFlat || 0).toFixed(2)}. Switch it on per register on the POS devices step.`
        : `Surcharge approval withdrawn for ${locationName || 'this location'}. The agreement stays on file.`);
      await load();
      onChanged();
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not save the surcharge approval.');
    } finally {
      setBusy(false);
    }
  };

  /** Open the signed PDF in its own tab — minted per click, never cached. */
  const openPdf = async (agreementId: string) => {
    try {
      const { downloadUrl } = await api.posNmi.getAgreementDownloadUrl(tenantId, locationId, agreementId);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not open the agreement.');
    }
  };

  return (
    /* GREEN WHEN IT IS LIVE. An approved location is charging its customers a
       fee right now, and that should be readable from across the room rather
       than inferred from a small grey chip. */
    <div className={`rounded-xl border ${approved ? 'border-emerald-300 bg-emerald-50/40' : 'border-stone-200 bg-white'}`}>
      <div className="flex items-start gap-3 px-3 py-2.5">
        <Percent size={15} className={`mt-0.5 shrink-0 ${approved ? 'text-emerald-600' : 'text-stone-400'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[12.5px] font-bold text-stone-800">Surcharge agreement</span>
            {/* THREE states, not two. "Could not read" must never render as
                "not approved" — see the note on `error`. */}
            {error
              ? <Chip tone="bad">could not read</Chip>
              : approved
                ? <Chip tone="ok">{'approved & active'}</Chip>
                : <Chip>not approved</Chip>}
            <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">
              {locationName || 'Location'}-wide
            </span>
          </div>
          <p className="mt-0.5 text-[11.5px] text-stone-500">
            {error
              ? <span className="font-medium text-ruby">{error}</span>
              : approved
                ? <>Every till at {locationName || 'this location'} may charge up to this ceiling — switch it on per register on the POS devices step.</>
                : 'Upload the signed agreement with its maximum rate and flat fee, set it active, then approve. Until it is approved, no register here can be switched to surcharge.'}
          </p>
          {error && (
            <button
              type="button"
              onClick={() => { void load(); }}
              className="mt-1 text-[11px] font-bold text-stone-600 underline underline-offset-2 hover:text-stone-900"
            >
              Retry
            </button>
          )}
        </div>
        <Toggle on={approved} disabled={gateway === null || busy} onChange={approve} label="Approve surcharge" />
      </div>

      {/*
        WHAT IS ACTUALLY APPROVED, in figures.

        The ceiling is the whole substance of the approval — it is what every
        till at this location is permitted to charge — so it is stated, not
        left to be found by opening a drawer and reading a row. The signed PDF
        opens from here too, because "what did we actually agree to" is the
        question this panel exists to answer.
      */}
      {approved && !error && (
        <div className="mx-3 mb-2 rounded-lg border border-emerald-200 bg-white px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Maximum rate</div>
              <div className="text-[17px] font-extrabold text-emerald-700">{Number(gateway?.maxSurchargeRate || 0).toFixed(2)}%</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Maximum flat fee</div>
              <div className="text-[17px] font-extrabold text-emerald-700">${Number(gateway?.maxSurchargeFlat || 0).toFixed(2)}</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-wide text-stone-400">Signed under</div>
              <div className="truncate text-[12.5px] font-semibold text-stone-800">
                {active.agreementName || active.fileName}
                {active.version ? <span className="font-normal text-stone-500"> · {active.version}</span> : null}
                {active.effectiveDate ? <span className="font-normal text-stone-500"> · from {active.effectiveDate}</span> : null}
              </div>
            </div>
            <button
              type="button"
              onClick={() => { void openPdf(active.id); }}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-[11.5px] font-semibold text-stone-700 hover:bg-stone-50"
            >
              <ExternalLink size={12} />
              Open the signed PDF
            </button>
          </div>
          {/* The merchant may set a LOWER rate under this; the ceiling is what
              they can never exceed. Said so the number is not mistaken for the
              rate actually being charged. */}
          <p className="mt-2 text-[11px] text-stone-500">
            The merchant may set a lower rate from their own settings — never higher. Surcharge is calculated on the
            subtotal only, never on tax.
          </p>
        </div>
      )}

      {/*
        THE AGREEMENTS, ALWAYS ON SCREEN.

        They were behind a disclosure, which is why an uploaded agreement
        looked like it had not arrived. There is no reason to hide them: this
        panel is the agreement panel. Any number may be on file and a new one
        can be added at any time — but exactly ONE is active, because one
        ceiling governs the location, and the list is where that one is chosen.
      */}
      <div className="border-t border-stone-100 px-3 py-2">
        {!error && (
          <SurchargeAgreementsList
            tenantId={tenantId}
            locationId={locationId}
            agreements={agreements}
            activeAgreementId={gateway?.activeSurchargeAgreementId || null}
            onChanged={() => { void refresh(); }}
            standalone
          />
        )}
        {!error && agreements.length > 1 && (
          <p className="mt-2 text-[11px] text-stone-500">
            One agreement is active at a time — setting another active archives the current one and moves every till at
            this location onto its ceiling.
          </p>
        )}
      </div>
    </div>
  );
};

export default SurchargeApprovalSection;
